// src/pages/RatesPage.js
import React, { useState, useEffect, useMemo } from "react";
import { Alert, Table } from "antd";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { CardTitle, SectionCard, StatusTag, TabBar, fmtDateTime, fmtMoney, tablePagination, useFlatPage } from "../components/ui";

const STATUS_CHIP = { Active: "available", Upcoming: "confirmed", Expired: "cancelled", None: "" };
const pick = (...v) => v.find((x) => x !== undefined && x !== null);

/**
 * Rate sheet for room types that have a discount set up.
 * The API's `discountedRate` is the discount PERCENTAGE that applies right now (0 when the promotion
 * is not active), not an amount. The money values are `netRate` and `totalPerNight`.
 */
const RatesPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("All");

  useEffect(() => {
    if (!hotelId) {
      setError("Hotel ID is not available.");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const response = await axiosInstance.get(`/Price/discounted/hotel/${hotelId}`);
        setRows(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        // The API answers 404 when no room type has a discount: that is an empty list, not a failure
        if (err.response?.status === 404) setRows([]);
        else setError(err.response?.data?.Message || err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [hotelId]);

  const data = useMemo(
    () =>
      rows.map((r, i) => {
        const standard = Number(pick(r.standardRate, r.StandardRate, 0));
        const pct = Number(pick(r.discountedRate, r.DiscountedRate, 0));
        return {
          key: `${pick(r.roomType, r.RoomType, "")}-${i}`,
          roomType: pick(r.roomType, r.RoomType, "—"),
          currency: pick(r.currency, r.Currency, "BWP"),
          standard,
          pct,
          net: Number(pick(r.netRate, r.NetRate, standard * (1 - pct / 100))),
          total: pick(r.totalPerNight, r.TotalPerNight, null),
          status: pick(r.discountStatus, r.DiscountStatus, "None"),
          start: pick(r.discountStart, r.DiscountStart, null),
          end: pick(r.discountEnd, r.DiscountEnd, null),
        };
      }),
    [rows]
  );

  const counts = useMemo(() => {
    const c = { All: data.length, Active: 0, Upcoming: 0, Expired: 0 };
    data.forEach((r) => {
      if (c[r.status] != null) c[r.status] += 1;
    });
    return c;
  }, [data]);

  const visible = tab === "All" ? data : data.filter((r) => r.status === tab);

  const columns = [
    { title: "Room type", dataIndex: "roomType", key: "roomType", render: (v) => <span className="hms-cell-main">{v}</span> },
    { title: "Standard rate", dataIndex: "standard", key: "standard", align: "right", render: (v, r) => fmtMoney(r.currency, v) },
    {
      title: "Discount now",
      dataIndex: "pct",
      key: "pct",
      align: "right",
      render: (v) => (v > 0 ? <span className="hms-cell-main">{v}%</span> : <span className="hms-muted">None</span>),
    },
    {
      title: "Rate after discount",
      dataIndex: "net",
      key: "net",
      align: "right",
      render: (v, r) => <span className="hms-cell-main">{fmtMoney(r.currency, v)}</span>,
    },
    {
      title: "Total / night",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (v, r) => (v != null ? fmtMoney(r.currency, v) : <span className="hms-muted">—</span>),
    },
    {
      title: "Promotion",
      key: "promotion",
      render: (_, r) => (
        <div className="hms-cell-stack">
          <span>
            <StatusTag status={STATUS_CHIP[r.status] ?? ""}>{r.status}</StatusTag>
          </span>
          {r.start && r.end && (
            <span className="hms-cell-sub">
              {fmtDateTime(r.start)} to {fmtDateTime(r.end)}
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="hms-page rates-page">
      {error && <Alert style={{ marginBottom: 12 }} type="error" showIcon title="Could not load rates" description={error} />}

      <SectionCard flush title={<CardTitle count={data.length}>Discounted rates</CardTitle>}>
        <TabBar
          tabs={[
            { key: "All", label: "All", count: counts.All },
            { key: "Active", label: "Active", count: counts.Active },
            { key: "Upcoming", label: "Upcoming", count: counts.Upcoming },
            { key: "Expired", label: "Expired", count: counts.Expired },
          ]}
          value={tab}
          onChange={setTab}
        />
        <Table
          className="hms-table"
          columns={columns}
          dataSource={visible}
          loading={loading}
          rowKey="key"
          pagination={tablePagination("rate", { hideOnSinglePage: true })}
          scroll={{ x: 900 }}
          locale={{ emptyText: <div className="hms-empty">No room types have a discount set up. Add one on the Room Price page.</div> }}
        />
        <p className="hms-card-hint" style={{ padding: "0 20px 14px" }}>
          "Discount now" is 0 when a promotion is upcoming or has expired. Rates are per night; the total includes VAT and levies.
        </p>
      </SectionCard>
    </div>
  );
};

export default RatesPage;
